import React, { useState, useLayoutEffect, useRef, useCallback } from "react";
import cmsUtils from "../../utils/cmsUtils";
import utils from "../../../utils";
import { SiteLink } from "components";
import { structureTable } from "../html/structureTable";

function getCellContent(cell) {
  return cell ? cell.innerHTML : "";
}

function restructureResponsiveTables(container) {
  const isMobile = window.innerWidth < 768;

  container.querySelectorAll("table.cms-table, table.table-row-header, table.table-col-header").forEach((table) => {
    const isRowHeader = table.classList.contains("table-row-header");
    const wrapper = table.parentElement.querySelector('.responsive-table-wrapper[data-source-table]');

    if (!isMobile) {
      table.style.display = "";
      if (wrapper) wrapper.style.display = "none";
      return;
    }

    table.style.display = "none";

    if (wrapper) {
      wrapper.style.display = "";
      return;
    }

    const rows = Array.from(table.querySelectorAll("tr"));
    if (rows.length < 2) return;

    const newWrapper = document.createElement("div");
    newWrapper.className = "responsive-table-wrapper";
    newWrapper.setAttribute("data-source-table", "true");

    if (isRowHeader) {
      const headerCells = Array.from(rows[0].querySelectorAll("th, td"));
      const dataRows = rows.slice(1);

      dataRows.forEach((row) => {
        const cells = Array.from(row.querySelectorAll("th, td"));
        const miniTable = document.createElement("table");
        miniTable.className = "responsive-table responsive-table--row-header";
        const tbody = document.createElement("tbody");

        headerCells.forEach((headerCell, idx) => {
          const tr = document.createElement("tr");
          const th = document.createElement("th");
          th.innerHTML = getCellContent(headerCell);
          const td = document.createElement("td");
          td.innerHTML = getCellContent(cells[idx]);
          tr.appendChild(th);
          tr.appendChild(td);
          tbody.appendChild(tr);
        });

        miniTable.appendChild(tbody);
        newWrapper.appendChild(miniTable);
      });
    } else {
      const headerCells = rows.map((row) => {
        const cells = row.querySelectorAll("th, td");
        return cells.length > 0 ? cells[0] : null;
      });

      const colCount = rows[0] ? rows[0].querySelectorAll("th, td").length : 0;

      for (let col = 1; col < colCount; col++) {
        const miniTable = document.createElement("table");
        miniTable.className = "responsive-table responsive-table--col-header";
        const tbody = document.createElement("tbody");

        rows.forEach((row, rowIdx) => {
          const cells = Array.from(row.querySelectorAll("th, td"));
          const tr = document.createElement("tr");
          const th = document.createElement("th");
          th.innerHTML = getCellContent(headerCells[rowIdx]);
          const td = document.createElement("td");
          td.innerHTML = getCellContent(cells[col]);
          tr.appendChild(th);
          tr.appendChild(td);
          tbody.appendChild(tr);
        });

        miniTable.appendChild(tbody);
        newWrapper.appendChild(miniTable);
      }
    }

    table.parentNode.insertBefore(newWrapper, table.nextSibling);
  });
}

export function AccordionPanel(props) {
  const item = props.item;
  const subItems = item.items || [];
  const cmsOption = props.cmsOption || {};
  const bgColor = cmsUtils.payload(item, "BgColor");
  const imageUrl = cmsUtils.payload(item, "ImageUrl");
  const backgroundTile = cmsUtils.payload(item, "BackgroundTile");

  const [selected, setSelected] = useState(null);
  const panelRef = useRef(null);
  const toggle = (i) => {
    if (selected === i) {
      return setSelected(null);
    }
    setSelected(i);
  };

  let bgUrl = null;
  if (imageUrl) bgUrl = utils.site.resourcePath(imageUrl);
  else if (backgroundTile)
    bgUrl = utils.site.resourcePath(
      `/dist/transparent-textures/textures/${backgroundTile}`
    );
  const cssClass = utils.classNames(
    "cms_item",
    "accordionPanel",
    "contentPanel--",
    item.cssClass || item.anchorName || ""
  );

  const subItemsElts = subItems.map((subItem, index) => {
    const answerContent = subItem.payloads[1].value;
    // Check if content is already HTML (contains HTML tags) or plain text
    const isHtmlContent = /<[a-z][\s\S]*>/i.test(answerContent);
    
    return (
      <div className='wrapper' key={index}>
        <div className={selected === index ? "accordion show" : "accordion"}>
          <div className='item'>
            <div
              className={selected === index ? "title show" : "title"}
              onClick={() => toggle(index)}
            >
              <h4>{subItem.payloads[0].value}</h4>
              <span className='open_button'></span>
            </div>
            <div className={selected === index ? "content show" : "content"}>
              <div className='content_text'
                dangerouslySetInnerHTML={{
                  __html: isHtmlContent 
                    ? answerContent 
                    : answerContent.replaceAll("\r\n", "<div><div/>")
                }}>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  });

  useLayoutEffect(() => {
    if (!panelRef.current) return;

    structureTable(panelRef.current);
    if (!cmsOption.isCmsEdit) {
      restructureResponsiveTables(panelRef.current);
    }
  }, [cmsOption, subItems]);

  const handleResize = useCallback(() => {
    if (panelRef.current && !cmsOption.isCmsEdit) {
      structureTable(panelRef.current);
      restructureResponsiveTables(panelRef.current);
    }
  }, [cmsOption]);

  React.useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  return (
    <>
      <section
        ref={panelRef}
        className={cssClass}
        style={{
          backgroundColor: `${bgColor}`,
          backgroundImage: bgUrl && utils.css.bgUrlStyle(bgUrl),
        }}
        data-cms-item-id={cmsOption.isCmsEdit && item.itemId}
        id={item.anchorName}
      >
        <div className='container'>
          <div className='accordion_container'>{subItemsElts}</div>
        </div>
      </section>
    </>
  );
}

function parseString(inputString) {
  const result = [];

  // Helper function to process bold text
  const processBoldText = (text) => {
    if (typeof text !== "string") return text;
    const boldParts = text.split(/(<b>.*?<\/b>)/g);
    return boldParts.map((part, index) => {
      if (part.startsWith("<b>") && part.endsWith("</b>")) {
        const content = part.replace(/<\/?b>/g, "");
        return <b key={`bold-${index}`}>{content}</b>;
      }
      return part;
    });
  };

  // Helper function to process links
  const processLinks = (content) => {
    if (typeof content !== "string") return content;
    const linkParts = content.split(/({[^}]+})/g);
    return linkParts.map((part, linkIndex) => {
      if (part.startsWith("{") && part.endsWith("}")) {
        const linkObject = {};
        part
          .slice(1, -1)
          .split(",")
          .forEach((pair) => {
            const [key, value] = pair
              .split(":")
              .map((s) => s.trim().replace(/['"]/g, ""));
            linkObject[key] = value;
          });
        return (
          <SiteLink
            key={`link-${linkIndex}`}
            to={linkObject.link || linkObject.url}
          >
            {linkObject.title || "Link"}
          </SiteLink>
        );
      }
      return part;
    });
  };

  // Helper function to process text with both bold and links
  const processTextContent = (text) => {
    const boldProcessed = processBoldText(text);
    if (Array.isArray(boldProcessed)) {
      return boldProcessed.map(processLinks).flat();
    }
    return processLinks(boldProcessed);
  };

  // Process paragraphs and lists
  const parts = inputString.split(
    /(<p>.*?<\/p>|\(\*[\s\S]+?\)|\(#[\s\S]+?\))/g
  );

  parts.forEach((part, index) => {
    if (part.startsWith("<p>") && part.endsWith("</p>")) {
      // Process paragraph
      const content = part.replace(/<\/?p>/g, "").trim();
      result.push(
        <p key={`paragraph-${index}`}>{processTextContent(content)}</p>
      );
    } else if (part.startsWith("(*") && part.endsWith(")")) {
      // Process unordered list
      const listItems = part
        .slice(2, -1)
        .split("*")
        .map((item) => item.trim())
        .filter(Boolean);
      result.push(
        <ul key={`ul-${index}`}>
          {listItems.map((item, itemIndex) => (
            <li key={itemIndex}>{processTextContent(item)}</li>
          ))}
        </ul>
      );
    } else if (part.startsWith("(#") && part.endsWith(")")) {
      // Process ordered list
      const listItems = part
        .slice(2, -1)
        .split("#")
        .map((item) => item.trim())
        .filter(Boolean);
      result.push(
        <ol key={`ol-${index}`}>
          {listItems.map((item, itemIndex) => (
            <li key={itemIndex}>{processTextContent(item)}</li>
          ))}
        </ol>
      );
    } else if (part.trim()) {
      // Add any other text
      result.push(processTextContent(part));
    }
  });

  return result.flat();
}

function ParsedString({ inputString }) {
  const parsedContent = parseString(inputString);
  return <>{parsedContent}</>;
}
